import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { Stack } from "@mui/material";
import MDBox from "lib/components/MDBox";
import { useState, useEffect, useRef } from "react";
import { formatAudioTimer } from "util/UtilityFunctions";
import WaveSurfer from "wavesurfer.js";
import RecordPlugin from "wavesurfer.js/dist/plugins/record.esm";
import DeleteIcon from "@mui/icons-material/Delete";
import { Pause, PlayArrowSharp, Stop } from "@mui/icons-material";
import { toast } from "react-toastify";
import { Audio as AudioService } from "services/Audio/Audio";
import AppModal from "ui/AppModal";
import AudioPermission from "./AudioPermission";

let record = undefined;
let wavesurfer = undefined;

const status = {
  INITIAL: "initial",
  RECORDING: "recording",
  PAUSED: "paused",
  RESUME: "resume",
  DONE: "done",
  ERROR: "error",
};

const Audio = ({ setAudioMessage, setMessageState }) => {
  const [playbackState, setPlaybackState] = useState("stopped");
  const [showPermissionModel, setShowPermissionModel] = useState(false);
  const [time, setTime] = useState(0);
  const [currStatus, setCurrStatus] = useState(status.INITIAL);
  const [showStopBtn, setShowStopBtn] = useState(true);

  const intervalRef = useRef();

  const init = async (startTimer, setMessageState, onError) => {
    wavesurfer = WaveSurfer.create({
      container: "#mic",
      waveColor: "rgb(68, 133, 190)",
      progressColor: "rgb(100, 0, 100)",
      autoCenter: true,
      height: 25,
      barHeight: 3,
    });

    // Initialize the Record plugin
    record = wavesurfer.registerPlugin(RecordPlugin.create());

    try {
      await AudioService.checkMicrophoneAvailability();
      await record.startRecording();
      setCurrStatus(status.RECORDING);
      startTimer();
    } catch (error) {
      onError(error);
    }

    record.on("record-end", (blob) => {
      console.log("Recording ended");
      clearInterval(intervalRef.current);
      wavesurfer.loadBlob(blob);
      setCurrStatus(status.DONE);
      setShowStopBtn(false);
      setPlaybackState("stopped");
      setMessageState((prev) => {
        return {
          ...prev,
          attachement: blob,
        };
      });
    });
    wavesurfer.on("finish", () => {
      console.log("Playback finished");
      setPlaybackState("stopped");
    });

    wavesurfer.on("pause", () => {
      console.log("Paused");
    });
  };

  const startTimer = () => {
    intervalRef.current = setInterval(() => {
      setTime((prevTime) => prevTime + 1);
    }, 10);
  };

  const stopTimer = () => {
    clearInterval(intervalRef.current);
    setTime(0);
  };

  const onError = (error) => {
    toast.error(
      "We are not able to detect a microphone. Please make sure you have a microphone connected to your device."
    );
    setCurrStatus(status.ERROR);
  };

  const pauseRecording = () => {
    record.pauseRecording();
    setCurrStatus(status.PAUSED);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const resumeRecording = () => {
    record.resumeRecording();
    setCurrStatus(status.RECORDING);
    startTimer();
  };

  const stopRecording = () => {
    record.stopRecording();
    setCurrStatus(status.DONE);
    stopTimer();
  };

  const deleteRecording = () => {
    record.destroy();
    setAudioMessage(null);
    setMessageState((prev) => {
      return {
        ...prev,
        attachement: null,
      };
    });
    setCurrStatus(status.INITIAL);
    setPlaybackState("stopped");
    setTime(0);
  };

  /* PlayBack Logic - Playback should manage pause play and 
  reset on playback completed */
  const togglePlayback = () => {
    if (playbackState === "stopped" || playbackState === "paused") {
      wavesurfer.playPause();
      setPlaybackState("playing");
    } else if (playbackState === "playing") {
      wavesurfer.playPause();
      setPlaybackState("paused");
    }
  };

  /* 🔹 Sync Timer with Playback */
  useEffect(() => {
    if (!wavesurfer) return;

    // Update timer as playback progresses
    wavesurfer.on("audioprocess", () => {
      setTime(Math.round(wavesurfer.getCurrentTime() * 100));
    });

    // Reset timer when playback finishes
    wavesurfer.on("finish", () => {
      console.log("Playback finished");
      setPlaybackState("stopped");
      setTime(0); // Reset timer after playback completes
    });

    return () => {
      wavesurfer.un("audioprocess");
      wavesurfer.un("finish");
    };
  }, [wavesurfer]);

  useEffect(() => {
    let isMounted = true;

    AudioService.askMicroPhonePermission()
      .then((res) => {
        if (!isMounted) return;

        if (res.state === "granted" || res.state === "prompt") {
          init(startTimer, setMessageState, onError);
        } else if (res.state === "denied") {
          setShowPermissionModel(true);
        }
      })
      .catch((err) => {
        toast.error(err);
      });

    return () => {
      isMounted = false;
      wavesurfer?.destroy();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };
  }, [setMessageState]);

  return (
    <>
      <MDBox
        className="chat-footer"
        display="flex"
        padding="12px"
        flexDirection="row"
        width="100%"
        justifyContent="center"
        alignItems="center"
      >
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={1}
        >
          <MDBox
            sx={{
              borderRadius: "20px",
              backgroundColor: "#e5eaf0",
              height: "48px",
              padding: "12px",
            }}
            display="flex"
            flexDirection="row"
            alignItems="center"
          >
            {/* Playback Button */}
            {currStatus === status.DONE && (
              <div style={{ display: "flex", alignItems: "center" }}>
                {playbackState === "stopped" || playbackState === "paused" ? (
                  <PlayArrowSharp
                    className="sidebar-icons"
                    sx={{
                      cursor: "pointer",
                      marginRight: "12px",
                    }}
                    onClick={togglePlayback}
                  />
                ) : (
                  <Pause
                    className="sidebar-icons"
                    sx={{
                      cursor: "pointer",
                      marginRight: "12px",
                    }}
                    onClick={togglePlayback}
                  />
                )}
              </div>
            )}

            <h4 style={{ marginRight: "15px" }}>{formatAudioTimer(time)}</h4>
            <div id="mic" style={{ width: "120px", overflow: "hidden" }}></div>

            {/* Recording Controls */}
            {currStatus === status.RECORDING && (
              <>
                <PauseCircleOutlineIcon
                  style={{ cursor: "pointer", marginLeft: "6px" }}
                  onClick={pauseRecording}
                  className="sidebar-icons"
                />
                {showStopBtn && (
                  <Stop
                    style={{ cursor: "pointer", marginLeft: "6px" }}
                    onClick={stopRecording}
                    className="sidebar-icons"
                  />
                )}
              </>
            )}

            {/* Pause/Resume and Stop */}
            {currStatus === status.PAUSED && (
              <>
                <PlayArrowSharp
                  className="sidebar-icons"
                  sx={{ cursor: "pointer" }}
                  onClick={resumeRecording}
                />
                {showStopBtn && (
                  <Stop
                    style={{ cursor: "pointer", marginLeft: "6px" }}
                    onClick={stopRecording}
                    className="sidebar-icons"
                  />
                )}
              </>
            )}

            {/* Delete Button */}
            {currStatus === status.DONE && (
              <DeleteIcon
                className="sidebar-icons"
                style={{ cursor: "pointer", marginLeft: "6px" }}
                onClick={deleteRecording}
              />
            )}
          </MDBox>
        </Stack>
      </MDBox>

      {showPermissionModel && (
        <AppModal
          customStyle={{ width: "500px", padding: "16px !important" }}
          onModalClose={() => setShowPermissionModel(false)}
          showCloseButton={false}
        >
          <AudioPermission />
        </AppModal>
      )}
    </>
  );
};

export default Audio;
